import Lenis from 'lenis';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

let lenisInstance = null;
let rafId = null;
let isFocusPaused = false;

const FOCUS_STOP_SELECTOR = 'input, textarea, select, [contenteditable="true"], [data-lenis-stop="true"]';

const shouldPauseForTarget = (target) => {
    if (!target) {
        return false;
    }

    return target.matches(FOCUS_STOP_SELECTOR);
};

const handleFocusIn = (event) => {
    if (!lenisInstance) {
        return;
    }

    if (shouldPauseForTarget(event.target)) {
        lenisInstance.stop();
        isFocusPaused = true;
    }
};

const handleFocusOut = (event) => {
    if (!lenisInstance || !isFocusPaused) {
        return;
    }

    if (shouldPauseForTarget(event.target)) {
        lenisInstance.start();
        isFocusPaused = false;
    }
};

function createLenis() {
    if (lenisInstance) {
        lenisInstance.destroy();
    }

    lenisInstance = new Lenis({
        duration: 1.45,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -12 * t)),
        lerp: 0.08,
        smoothWheel: true,
        wheelMultiplier: 0.85,
        smoothTouch: false,
        gestureDirection: 'vertical',
        normalizeWheel: true
    });

    window.lenis = lenisInstance;

    lenisInstance.on('scroll', () => {
        ScrollTrigger.update();
    });

    const raf = (time) => {
        lenisInstance.raf(time);
        rafId = requestAnimationFrame(raf);
    };

    rafId = requestAnimationFrame(raf);

}

function destroyLenis() {
    if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
    }

    if (lenisInstance) {
        lenisInstance.destroy();
        lenisInstance = null;
    }

    isFocusPaused = false;
}

function initSmoothScroll() {
    destroyLenis();
    createLenis();
}

function resolveHashTarget(hash) {
    if (!hash || hash.length < 2) {
        return null;
    }

    let id;
    try {
        id = decodeURIComponent(hash.slice(1));
    } catch (e) {
        id = hash.slice(1);
    }

    return document.getElementById(id) || document.querySelector(hash);
}

function scrollToHash(hash, options = {}) {
    const target = resolveHashTarget(hash);
    if (!target || !lenisInstance) {
        return false;
    }

    if (lenisInstance.isStopped) {
        lenisInstance.start();
    }

    const defaults = {
        duration: 1.4,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -12 * t)),
        lock: true,
        force: true
    };

    lenisInstance.scrollTo(target, { ...defaults, ...options });
    return true;
}

function handleInitialHash() {
    if (!window.location.hash) {
        return;
    }

    if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
    }

    const initialHash = window.location.hash;

    const jump = () => {
        scrollToHash(initialHash, { immediate: true, force: true, lock: true });
    };

    if (document.readyState === 'complete') {
        requestAnimationFrame(jump);
    } else {
        window.addEventListener('load', () => {
            requestAnimationFrame(jump);
        }, { once: true });
    }
}

function handleAnchorClick(event) {
    if (event.defaultPrevented || event.button !== 0) {
        return;
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
    }

    const link = event.target.closest && event.target.closest('a[href]');
    if (!link) {
        return;
    }
    if (link.target && link.target !== '' && link.target !== '_self') {
        return;
    }
    if (link.hasAttribute('download')) {
        return;
    }

    let url;
    try {
        url = new URL(link.href, window.location.href);
    } catch (e) {
        return;
    }

    if (url.origin !== window.location.origin) {
        return;
    }
    if (url.pathname !== window.location.pathname) {
        return;
    }
    if (!url.hash || url.hash.length < 2) {
        return;
    }

    const target = resolveHashTarget(url.hash);
    if (!target) {
        return;
    }

    event.preventDefault();

    if (window.location.hash !== url.hash) {
        window.history.pushState(null, '', url.hash);
    }

    scrollToHash(url.hash);
}

function handleHashChange() {
    scrollToHash(window.location.hash);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSmoothScroll);
} else {
    initSmoothScroll();
}

handleInitialHash();

document.addEventListener('click', handleAnchorClick);
window.addEventListener('hashchange', handleHashChange);
window.addEventListener('focusin', handleFocusIn);
window.addEventListener('focusout', handleFocusOut);